const DEFAULT_CHARS =
  'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

export interface ScrambleOptions {
  duration?: number;
  chars?: string;
  delay?: number;
  /** Interval in ms between character updates during scramble phase */
  tickInterval?: number;
}

function getReduceMotion(): boolean {
  if (typeof window === 'undefined') return true;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function randomChar(chars: string): string {
  return chars[Math.floor(Math.random() * chars.length)];
}

/**
 * Scramble/decode text animation: cycles random characters then reveals final text left-to-right.
 * Respects prefers-reduced-motion (shows final text immediately).
 */
export function scrambleElement(
  el: HTMLElement,
  options: ScrambleOptions = {}
): void {
  const reduceMotion = getReduceMotion();
  const finalText = (el.textContent || '').trim();
  if (!finalText) return;

  const {
    duration = 0.8,
    chars = DEFAULT_CHARS,
    delay = 0,
  } = options;

  if (reduceMotion) {
    el.textContent = finalText;
    return;
  }

  let rafId: number | null = null;
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  let startTime: number = 0;
  const len = finalText.length;
  let cancelled = false;

  const cancel = (): void => {
    cancelled = true;
    if (timeoutId !== null) clearTimeout(timeoutId);
    if (rafId !== null) cancelAnimationFrame(rafId);
    timeoutId = null;
    rafId = null;
  };
  (el as any).__scrambleCancel = cancel;

  const run = (): void => {
    if (cancelled) return;
    const now = performance.now();
    if (startTime === 0) startTime = now;
    const elapsed = (now - startTime) / 1000;

    if (elapsed < delay) {
      rafId = requestAnimationFrame(run);
      return;
    }

    const adjusted = elapsed - delay;
    if (adjusted >= duration) {
      el.textContent = finalText;
      return;
    }

    const revealProgress = Math.min(1, adjusted / duration);
    const revealCount = Math.floor(revealProgress * len);

    let str = '';
    for (let i = 0; i < len; i++) {
      if (finalText[i] === ' ') {
        str += ' ';
      } else if (i < revealCount) {
        str += finalText[i];
      } else {
        str += randomChar(chars);
      }
    }
    el.textContent = str;
    rafId = requestAnimationFrame(run);
  };

  if (delay > 0) {
    timeoutId = setTimeout(() => {
      timeoutId = null;
      rafId = requestAnimationFrame(run);
    }, delay * 1000);
  } else {
    rafId = requestAnimationFrame(run);
  }
}

/**
 * Cancel any running scramble animation on an element (e.g. on dispose).
 */
export function cancelScramble(el: HTMLElement): void {
  const cancel = (el as any).__scrambleCancel;
  if (typeof cancel === 'function') {
    cancel();
    delete (el as any).__scrambleCancel;
  }
}
